// CogniPrint Analytics Service — Taxonomy v1.0
// All events go through trackEvent(). Batched to Firestore every 5s or on unload.

import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import type { AnalyticsEvent, BaseEventProperties } from '../types/analytics';
import { ANALYTICS_EVENTS } from '../constants/analytics-events';

const APP_VERSION = '1.0.0';

// ─── Session Management ───

export function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem('cogniprint_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('cogniprint_session_id', sessionId);
  }
  return sessionId;
}

function getDeviceType(): 'mobile' | 'desktop' | 'tablet' {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

export function getBaseProperties(): BaseEventProperties {
  const user = auth.currentUser;
  return {
    timestamp: Date.now(),
    userId: user?.uid ?? 'anonymous',
    sessionId: getOrCreateSessionId(),
    deviceType: getDeviceType(),
    appVersion: APP_VERSION,
    tier: 'free', // Updated at call site if available
  };
}

// ─── Event Batching ───

let eventBuffer: Array<{ eventId: string; data: Record<string, unknown> }> = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushEvents().catch(console.error);
  }, 5000);
}

export async function flushEvents(): Promise<void> {
  if (eventBuffer.length === 0) return;
  const batch = eventBuffer;
  eventBuffer = [];

  const userId = auth.currentUser?.uid;
  if (!userId) return;

  await Promise.all(
    batch.map(({ eventId, data }) =>
      setDoc(doc(db, 'analytics', userId, 'events', eventId), data).catch(console.error),
    ),
  );
}

// Flush on page unload. Guard on addEventListener too: React Native defines a
// `window` global WITHOUT addEventListener, so a bare `typeof window` check
// passes there and then throws. (web has it; RN safely skips these listeners.)
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('beforeunload', () => {
    flushEvents().catch(() => {});
  });
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushEvents().catch(() => {});
    }
  });
}

// ─── Core Track Function ───

export async function trackEvent(event: AnalyticsEvent): Promise<void> {
  const eventId = `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  eventBuffer.push({
    eventId,
    data: {
      eventId,
      eventName: event.name,
      ...event.properties,
    },
  });

  scheduleFlush();
}

// ─── Convenience Methods ───

export async function trackPageView(pageName: string, referrer?: string): Promise<void> {
  await trackEvent({
    name: ANALYTICS_EVENTS.APP_PAGE_VIEWED,
    properties: {
      ...getBaseProperties(),
      pageName,
      referrer: referrer ?? null,
    },
  });
}

export async function trackError(
  errorType: string,
  errorCode: string | null,
  context: string,
  recoverable: boolean,
): Promise<void> {
  await trackEvent({
    name: ANALYTICS_EVENTS.APP_ERROR_ENCOUNTERED,
    properties: {
      ...getBaseProperties(),
      errorType,
      errorCode,
      context,
      recoverable,
    },
  });
}
